import { useIsMobileHook } from '@teliaee/telia-common-components';
import { TranslationsContext } from '@teliaee/telia-translate';
import React, { useContext } from 'react';
import { AnalyticsTrigger, Button, Icon, EqualHeight, Grid, GridColumn, Heading, Card, CardContent, List, ListItem, Separator, Tag } from 'telia-front-react';
import Config from '../../Config';
import { twebTranslateOrEmpty } from '../../util/TranslateUtil';

interface Props {
  missing: boolean;
  suffix: 'mobile' | 'tv' | 'internet';
  mainIcon: string;
  ctaUrl: string;
  offerIcon: string;
  children: JSX.Element;
  ctaText?: string;
  isFullHouse?: boolean;
}

const Telia1OfferCard = (props: Props): JSX.Element => {
  const { translate, translateOrEmpty } = useContext(TranslationsContext);
  const { missing, suffix, mainIcon, ctaUrl, offerIcon, children, ctaText, isFullHouse } = props;

  const { isMobile } = useIsMobileHook();

  const renderContent = (): JSX.Element => (
    <>
      {missing && suffix === 'mobile' && twebTranslateOrEmpty(translateOrEmpty, 'personalOffers.telia1.card.mobile.discount') && (
        <Tag size="tiny" text={translate('personalOffers.telia1.card.mobile.discount')} purpose="campaign-3" />
      )}
      <Separator color="invisible" spacing="xsmall" />
      {!missing && (
        <>
          <p className={isFullHouse ? 'text-tiny text-primary' : ''}>{translate('personalOffers.telia1.card.promo')}</p>
          <Separator color="invisible" spacing={!isMobile && !isFullHouse ? 'xsmall' : 'xxsmall'} />
        </>
      )}
      <List type="icon">
        <ListItem icon={!isMobile ? offerIcon : undefined} className={isFullHouse ? 'text-tiny text-primary' : ''}>
          {children}
        </ListItem>
      </List>
    </>
  );

  const renderFooter = (): JSX.Element => {
    return !missing ? null : (
      <>
        <Separator spacing="xsmall" color="invisible" />
        {!!twebTranslateOrEmpty(translateOrEmpty, `personalOffers.telia1.card.campaignRules.${suffix}.isMissing-${missing}`) && (
          <>
            <AnalyticsTrigger trigger={{ id: `Link - Telia1 campaign rules ${suffix}`, callback: 'onClick' }}>
              <Button
                url={translate(`personalOffers.telia1.card.campaignRules.${suffix}.isMissing-${missing}`)}
                iconClass="text-color-normal"
                target="_blank"
                iconLeft="#file-pdf"
                text={translate('personalOffers.telia1.card.campaignRules.label')}
                link={true}
                className="text-small"
              />
            </AnalyticsTrigger>
            <Separator spacing="xsmall" color="invisible" />
          </>
        )}
        <Grid verticalGutter="small" align={['center-xs']}>
          <GridColumn width={['max']}>
            <AnalyticsTrigger trigger={{ id: `Link - Telia1 ${suffix}`, callback: 'onClick' }}>
              <Button url={ctaUrl} text={ctaText || translate('personalOffers.telia1.card.cta.label')} iconRight="#arrow-right" block={true} />
            </AnalyticsTrigger>
          </GridColumn>
          {!!twebTranslateOrEmpty(translateOrEmpty, `personalOffers.telia1.card.infoCta.url.${suffix}`) && (
            <GridColumn width={['min']}>
              <Separator spacing="xxsmall" color="invisible" />
              <Button
                url={translate(`personalOffers.telia1.card.infoCta.url.${suffix}`, { baseUrl: Config.teliaWebUrl })}
                iconRight="#arrow-right"
                text={translate('personalOffers.telia1.card.infoCta.label')}
                link={true}
              />
            </GridColumn>
          )}
        </Grid>
      </>
    );
  };

  return (
    <Card background="neutral-bg-level-1">
      <CardContent
        tagTop={{
          text: missing ? translate('personalOffers.telia1.badge.missing') : translate('personalOffers.telia1.badge.exists'),
          purpose: missing ? 'error' : 'success'
        }}
      >
        <EqualHeight group="telia1-header">
          <Icon size="sm" modifiers={mainIcon} />
          {isFullHouse ? (
            <p className="text-bold">{translate(`personalOffers.telia1.card.${suffix}`)}</p>
          ) : (
            <Heading className="text-break h4" level={2}>
              {translate(`personalOffers.telia1.card.${suffix}`)}
            </Heading>
          )}
        </EqualHeight>
        <EqualHeight group="telia1-content">{renderContent()}</EqualHeight>
        <EqualHeight group="telia1-footer">{renderFooter()}</EqualHeight>
      </CardContent>
    </Card>
  );
};
export default Telia1OfferCard;
